import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Docs.css";
import {
  AgentPayload,
  AgentRecord,
  deleteAgent,
  fetchAgents,
  saveAgent,
} from "../config/api";
import JsonEditor from "../components/JsonEditor";

type SelectedAgentId = string | "new" | null;

const emptyAgent: AgentFormState = {
  agentKey: "",
  name: "",
  description: "",
  prompt: "",
  enabled: true,
  payloadTemplateText: "",
  allowedFieldsText: "",
  lockedFieldsText: "",
  prefetchEndpoint: "",
  actionEndpoint: "",
  actionMethod: "POST",
  confirmLabel: "Approve",
  successMarkdown: "Agent action completed.",
  tags: ["Agent","Task"],
  pageMode: "prefixed",
  pageSuffix: "",
  pageCustom: "",
};

const DEFAULT_PAGE_PREFIX = "/lot/{lotId}/";

type AgentFormState = {
  agentKey: string;
  name: string;
  description: string;
  prompt: string;
  enabled: boolean;
  payloadTemplateText: string;
  allowedFieldsText: string;
  lockedFieldsText: string;
  prefetchEndpoint: string;
  actionEndpoint: string;
  actionMethod: string;
  confirmLabel: string;
  successMarkdown: string;
  tags: string[];
  pageMode: "prefixed" | "custom";
  pageSuffix: string;
  pageCustom: string;
};

const formatJsonText = (value?: any) => {
  if (!value || (typeof value === "object" && Object.keys(value).length === 0)) {
    return "";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
};

const buildFormState = (agent?: AgentRecord | null): AgentFormState => ({
  agentKey: agent?.agentKey ?? "",
  name: agent?.name ?? "",
  description: agent?.description ?? "",
  prompt: agent?.prompt ?? "",
  enabled: agent?.enabled ?? true,
  payloadTemplateText: formatJsonText(agent?.payloadTemplate),
  allowedFieldsText: (agent?.allowedFields ?? []).join("\n"),
  lockedFieldsText: (agent?.lockedFields ?? []).join("\n"),
  prefetchEndpoint: agent?.prefetchEndpoint ?? "",
  actionEndpoint: agent?.actionEndpoint ?? "",
  actionMethod: agent?.actionMethod ?? "POST",
  confirmLabel: agent?.confirmLabel ?? "Confirm",
  successMarkdown: agent?.successMarkdown ?? "✅ Agent action completed.",
  tags: agent?.tags ?? [],
  ...buildPageState(agent?.page),
});

const buildPageState = (page?: string | null) => {
  const trimmed = page?.trim() ?? "";
  if (!trimmed) {
    return {
      pageMode: "prefixed" as const,
      pageSuffix: "",
      pageCustom: "",
    };
  }
  if (trimmed.startsWith(DEFAULT_PAGE_PREFIX)) {
    const suffix = trimmed.slice(DEFAULT_PAGE_PREFIX.length).replace(/^\/+/, "");
    return {
      pageMode: "prefixed" as const,
      pageSuffix: suffix,
      pageCustom: "",
    };
  }
  return {
    pageMode: "custom" as const,
    pageSuffix: "",
    pageCustom: trimmed,
  };
};

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<SelectedAgentId>(null);
  const [editingAgentId, setEditingAgentId] = useState<SelectedAgentId>(null);
  const [formState, setFormState] = useState<AgentFormState>(emptyAgent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");

  const selectedAgent = useMemo(() => {
    if (!selectedAgentId || selectedAgentId === "new") {
      return null;
    }
    return agents.find((agent) => agent.agentId === selectedAgentId) ?? null;
  }, [selectedAgentId, agents]);

  const isEditing = editingAgentId !== null;

  useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoading(true);
        const data = await fetchAgents();
        setAgents(data);
        if (data.length > 0) {
          setSelectedAgentId(data[0].agentId);
          setFormState(buildFormState(data[0]));
        }
      } catch (err) {
        console.error("SuperadminFE: failed to load agents", err);
        setError("Unable to load agents. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    loadAgents();
  }, []);

  const refreshAgents = async (keepSelection?: string) => {
    const data = await fetchAgents();
    setAgents(data);
    if (data.length === 0) {
      setSelectedAgentId(null);
      setFormState(emptyAgent);
      return;
    }
    const nextSelection = keepSelection && data.some((agent) => agent.agentId === keepSelection)
      ? keepSelection
      : data[0].agentId;
    setSelectedAgentId(nextSelection);
    const active = data.find((agent) => agent.agentId === nextSelection);
    setFormState(buildFormState(active));
  };

  const handleSelectAgent = (agentId: string) => {
    if (isEditing && editingAgentId !== agentId) {
      return;
    }
    setSelectedAgentId(agentId);
    const agent = agents.find((item) => item.agentId === agentId);
    setFormState(buildFormState(agent));
    setEditingAgentId(null);
    setError(null);
  };

  const handleNewAgent = () => {
    setSelectedAgentId("new");
    setEditingAgentId("new");
    setFormState(emptyAgent);
    setError(null);
  };

  const handleStartEditing = () => {
    if (!selectedAgentId) {
      return;
    }
    setEditingAgentId(selectedAgentId);
    if (selectedAgentId === "new") {
      setFormState(emptyAgent);
    } else if (selectedAgent) {
      setFormState(buildFormState(selectedAgent));
    }
    setError(null);
  };

  const handleCancelEdit = () => {
    if (selectedAgentId === "new") {
      if (agents[0]) {
        setSelectedAgentId(agents[0].agentId);
        setFormState(buildFormState(agents[0]));
      } else {
        setSelectedAgentId(null);
        setFormState(emptyAgent);
      }
    } else if (selectedAgent) {
      setFormState(buildFormState(selectedAgent));
    }
    setEditingAgentId(null);
    setError(null);
  };

  const handleToggleEnabled = () => {
    setFormState((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleTogglePageMode = () => {
    if (!isEditing) {
      return;
    }
    setFormState((prev) => {
      if (prev.pageMode === "prefixed") {
        const full = prev.pageSuffix
          ? `${DEFAULT_PAGE_PREFIX}${prev.pageSuffix.replace(/^\/+/, "")}`
          : prev.pageCustom;
        return {
          ...prev,
          pageMode: "custom",
          pageCustom: full ?? "",
        };
      }
      const candidate = prev.pageCustom ?? "";
      const suffix = candidate.startsWith(DEFAULT_PAGE_PREFIX)
        ? candidate.slice(DEFAULT_PAGE_PREFIX.length)
        : candidate;
      return {
        ...prev,
        pageMode: "prefixed",
        pageSuffix: suffix.replace(/^\/+/, ""),
        pageCustom: "",
      };
    });
  };

  const handleFormChange = (field: keyof AgentFormState, value: string | boolean) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      tags: Array.from(new Set([...(prev.tags ?? []), trimmed])),
    }));
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setFormState((prev) => ({
      ...prev,
      tags: (prev.tags ?? []).filter((item) => item !== tag),
    }));
  };

  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleAddTag();
    }
  };

  const parseJsonText = (text: string, fieldLabel: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      return {};
    }
    try {
      return JSON.parse(trimmed);
    } catch (err) {
      throw new Error(`${fieldLabel} must be valid JSON.`);
    }
  };

  const parseFieldList = (text: string) => text
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  const handleSave = async () => {
    if (!formState.agentKey.trim()) {
      setError("Agent key is required.");
      return;
    }
    if (!formState.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!formState.prompt.trim()) {
      setError("Agent prompt is required.");
      return;
    }

    let payloadTemplate: any;
    try {
      payloadTemplate = parseJsonText(formState.payloadTemplateText, "Payload template");
    } catch (jsonError: any) {
      setError(jsonError.message);
      return;
    }

    const endpoint = formState.actionEndpoint.trim();
    const prefetchEndpoint = formState.prefetchEndpoint.trim();
    let pageValue: string | undefined;
    if (formState.pageMode === "prefixed") {
      const suffix = formState.pageSuffix.trim().replace(/^\/+/, "");
      if (suffix) {
        pageValue = `${DEFAULT_PAGE_PREFIX}${suffix}`;
      }
    } else {
      const custom = formState.pageCustom.trim();
      if (custom) {
        pageValue = custom;
      }
    }
    const payload: AgentPayload = {
      agentId: selectedAgentId === "new" ? undefined : selectedAgentId ?? undefined,
      agentKey: formState.agentKey.trim(),
      name: formState.name.trim(),
      description: formState.description.trim(),
      prompt: formState.prompt.trim(),
      enabled: formState.enabled,
      payloadTemplate,
      tags: formState.tags,
      allowedFields: parseFieldList(formState.allowedFieldsText),
      lockedFields: parseFieldList(formState.lockedFieldsText),
      page: pageValue,
      prefetchEndpoint: prefetchEndpoint.length > 0 ? prefetchEndpoint : undefined,
      actionEndpoint: endpoint.length > 0 ? endpoint : undefined,
      actionMethod: formState.actionMethod,
      confirmLabel: formState.confirmLabel.trim() || "Confirm",
      successMarkdown: formState.successMarkdown.trim(),
    };

    setSaving(true);
    setError(null);
    try {
      const saved = await saveAgent(payload);
      await refreshAgents(saved.agentId);
      setEditingAgentId(null);
    } catch (err) {
      console.error("SuperadminFE: failed to save agent", err);
      setError("Unable to save the agent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const promptDelete = () => {
    if (!selectedAgentId || selectedAgentId === "new") {
      return;
    }
    setConfirmDeleteId(selectedAgentId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) {
      return;
    }
    setSaving(true);
    try {
      await deleteAgent(confirmDeleteId);
      setConfirmDeleteId(null);
      await refreshAgents();
      setEditingAgentId(null);
    } catch (err) {
      console.error("SuperadminFE: failed to delete agent", err);
      setError("Unable to delete the agent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) {
      return "--";
    }
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const renderDetail = () => {
    if (loading) {
      return <div className="docs-detail-empty">Loading agents...</div>;
    }
    if (!selectedAgentId) {
      return <div className="docs-detail-empty">Select an agent to view details.</div>;
    }
    return (
      <div className="docs-detail-card">
        <div className="docs-field">
          <label htmlFor="agent-key">Agent Key</label>
          <input
            id="agent-key"
            type="text"
            placeholder="unique-agent-key"
            value={formState.agentKey}
            onChange={(event) => handleFormChange("agentKey", event.target.value)}
            disabled={selectedAgentId !== "new" && !isEditing}
          />
          {selectedAgentId !== "new" && (
            <small className="agents-hint">Keys are fixed after creation.</small>
          )}
        </div>
        <div className="docs-field">
          <label htmlFor="agent-name">Name</label>
          <input
            id="agent-name"
            type="text"
            placeholder="Agent Name"
            value={formState.name}
            onChange={(event) => handleFormChange("name", event.target.value)}
            disabled={!isEditing}
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-description">Description</label>
          <textarea
            id="agent-description"
            placeholder="Brief description of the agent..."
            rows={3}
            value={formState.description}
            onChange={(event) => handleFormChange("description", event.target.value)}
            disabled={!isEditing}
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-prompt">Agent Prompt</label>
          <textarea
            id="agent-prompt"
            rows={8}
            value={formState.prompt}
            onChange={(event) => handleFormChange("prompt", event.target.value)}
            disabled={!isEditing}
            placeholder="Describe the agent's goals and required output format..."
          />
        </div>
        <div className="docs-field">
          <label>Tags</label>
          <div className={`docs-tags ${isEditing ? "editing" : ""}`}>
            <div className="docs-tag-chips">
              {formState.tags.length === 0 && !isEditing && <span className="docs-tag-placeholder">No tags</span>}
              {formState.tags.map((tag) => (
                <span key={tag} className="docs-tag">
                  {tag}
                  {isEditing && (
                    <button type="button" onClick={() => handleRemoveTag(tag)} aria-label={`Remove ${tag}`}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            {isEditing && (
              <div className="docs-tag-input">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(event) => setTagInput(event.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add tag and press Enter"
                />
                <button
                  type="button"
                  className="docs-btn docs-btn-secondary"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="docs-field agents-toggle">
          <label htmlFor="agent-enabled">Enabled</label>
          <label className="switch">
            <input
              id="agent-enabled"
              type="checkbox"
              checked={formState.enabled}
              onChange={handleToggleEnabled}
              disabled={!isEditing}
            />
            <span className="slider" />
          </label>
          <span className="agents-toggle-status">{formState.enabled ? "Enabled" : "Disabled"}</span>
        </div>
        <div className="docs-field">
          <label htmlFor="agent-payload-template">Payload Template JSON</label>
          <span className="docs-field-hint">
            JSON structure the agent should fill before the confirmation button fires.
          </span>
          <JsonEditor
            id="agent-payload-template"
            value={formState.payloadTemplateText}
            onChange={(code) => handleFormChange("payloadTemplateText", code)}
            disabled={!isEditing}
            rows={8}
            placeholder="{ }"
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-allowed-fields">Allowed Fields</label>
          <span className="docs-field-hint">
            Only these keys can be modified by the agent. Separate with commas or new lines.
          </span>
          <textarea
            id="agent-allowed-fields"
            rows={3}
            value={formState.allowedFieldsText}
            onChange={(event) => handleFormChange("allowedFieldsText", event.target.value)}
            disabled={!isEditing}
            placeholder={"field1\nfield2"}
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-locked-fields">Locked Fields</label>
          <span className="docs-field-hint">
            Keys listed here will always be forced to match the prefetched snapshot.
          </span>
          <textarea
            id="agent-locked-fields"
            rows={3}
            value={formState.lockedFieldsText}
            onChange={(event) => handleFormChange("lockedFieldsText", event.target.value)}
            disabled={!isEditing}
            placeholder={"field1\nfield2"}
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-view-page">Agent View Page</label>
          <span className="docs-field-hint">
            Determines where the Operator is sent after approving the action. The lot prefix is locked.
          </span>
          {formState.pageMode === "prefixed" ? (
            <div className="agents-path-input">
              <span className="agents-path-prefix">{DEFAULT_PAGE_PREFIX}</span>
              <input
                id="agent-view-page"
                type="text"
                value={formState.pageSuffix}
                onChange={(event) => handleFormChange("pageSuffix", event.target.value)}
                disabled={!isEditing}
                placeholder="settings/pricing"
              />
            </div>
          ) : (
            <input
              id="agent-view-page-custom"
              type="text"
              value={formState.pageCustom}
              onChange={(event) => handleFormChange("pageCustom", event.target.value)}
              disabled={!isEditing}
              placeholder="/lot/{lotId}/settings/pricing"
            />
          )}
          <button
            type="button"
            className="docs-btn docs-btn-link agents-path-toggle"
            onClick={handleTogglePageMode}
            disabled={!isEditing}
          >
            {formState.pageMode === "prefixed" ? "Use custom URL" : "Use lot-relative path"}
          </button>
        </div>
        <div className="docs-field">
          <label htmlFor="agent-prefetch">Prefetch Endpoint (optional)</label>
          <span className="docs-field-hint">
            GET endpoint triggered before the model responds. Supports placeholders like <code>{"{lotId}"}</code>.
          </span>
          <input
            id="agent-prefetch"
            type="text"
            value={formState.prefetchEndpoint}
            onChange={(event) => handleFormChange("prefetchEndpoint", event.target.value)}
            disabled={!isEditing}
            placeholder="ex: /lotpricing/get-latest-pricing-by-lot-id/{lotId}"
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-endpoint">Action Endpoint</label>
          <input
            id="agent-endpoint"
            type="text"
            value={formState.actionEndpoint}
            onChange={(event) => handleFormChange("actionEndpoint", event.target.value)}
            disabled={!isEditing}
            placeholder="ex: /lotpricing/update-pricing-by-lot-id/{lotId}/{pricingId}"
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-method">HTTP Method</label>
          <select
            id="agent-method"
            value={formState.actionMethod}
            onChange={(event) => handleFormChange("actionMethod", event.target.value)}
            disabled={!isEditing}
          >
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <div className="docs-field">
          <label htmlFor="agent-confirm-label">Confirmation Button Label</label>
          <input
            id="agent-confirm-label"
            type="text"
            value={formState.confirmLabel}
            onChange={(event) => handleFormChange("confirmLabel", event.target.value)}
            disabled={!isEditing}
            placeholder="Confirm"
          />
        </div>
        <div className="docs-field">
          <label htmlFor="agent-success">Success Message</label>
          <textarea
            id="agent-success"
            rows={3}
            value={formState.successMarkdown}
            onChange={(event) => handleFormChange("successMarkdown", event.target.value)}
            disabled={!isEditing}
            placeholder="Agent action completed."
          />
        </div>
        {selectedAgent && (
          <div className="docs-meta">
            <span>Created: {formatDate(selectedAgent.createdOn)}</span>
            <span>Updated: {formatDate(selectedAgent.modifiedOn)}</span>
          </div>
        )}
        <div className="docs-action-row">
          {isEditing ? (
            <>
              <button className="docs-btn docs-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                Save
              </button>
              <button className="docs-btn docs-btn-ghost" type="button" onClick={handleCancelEdit}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="docs-btn docs-btn-secondary" type="button" onClick={handleStartEditing} disabled={!selectedAgentId}>
                Edit
              </button>
              <button
                className="docs-btn docs-btn-danger"
                type="button"
                onClick={promptDelete}
                disabled={!selectedAgentId || selectedAgentId === "new"}
              >
                Delete
              </button>
            </>
          )}
        </div>
        {error && <div className="docs-error">{error}</div>}
      </div>
    );
  };

  return (
    <div className="docs-page">
      <header className="docs-header">
        <div className="docs-header-content">
          <div className="docs-logo" onClick={() => navigate("/dashboard")} role="button" tabIndex={0}>
            <img src="/assets/LogotypeSuperAdminHorizontal.svg" alt="Parallel Superadmin" />
          </div>
          <div className="docs-header-actions">
            <button type="button" className="docs-btn docs-btn-ghost" onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
            <button
              type="button"
              className="docs-btn docs-btn-primary"
              onClick={handleNewAgent}
              disabled={isEditing && editingAgentId !== "new"}
            >
              New Agent
            </button>
          </div>
        </div>
      </header>

      <main className="docs-main">
        <aside className="docs-list-panel">
          {agents.length === 0 && !loading ? (
            <div className="docs-empty">No agents found. Create the first agent.</div>
          ) : (
            <ul>
              {agents.map((agent) => (
                <li
                  key={agent.agentId}
                  className={selectedAgentId === agent.agentId ? "active" : ""}
                  onClick={() => handleSelectAgent(agent.agentId)}
                >
                  <div className="docs-list-title">{agent.name || agent.agentKey}</div>
                  <div className="docs-list-meta">
                    <span>{agent.agentKey}</span>
                    <div className="docs-list-tags">
                      <span>{agent.enabled ? "Enabled" : "Disabled"}</span>
                      {agent.actionMethod && <span>{agent.actionMethod}</span>}
                    </div>
              </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
        <section className="docs-detail-panel">{renderDetail()}</section>
      </main>

      {confirmDeleteId && (
        <div className="docs-modal-overlay" role="dialog" aria-modal="true">
          <div className="docs-modal">
            <h3>Delete agent?</h3>
            <p>This action cannot be undone. The agent will no longer be available in chat.</p>
            <div className="docs-modal-actions">
              <button type="button" className="docs-btn docs-btn-ghost" onClick={() => setConfirmDeleteId(null)}>
                Cancel
              </button>
              <button type="button" className="docs-btn docs-btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;
